import type { ReactNode } from 'react';

import useSWR from 'swr';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';

import { Box, Stack, Switch, Button, MenuItem, TextField, Typography, DialogContent, DialogActions } from '@mui/material';

import { fetcher, endpoints, updateCall } from 'src/lib/axios';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------
// Notifications tab of the Account Settings dialog. Reads and writes the user's real preferences
// (GET/PUT /api/Account/NotificationPreferences); the scoping banner reads the real follow count.
// Timezone seeds from the browser locale until the user has saved one. Following a job is the
// subscription; these controls only govern format and cadence.
// ----------------------------------------------------------------------

type NotificationsTabProps = { onClose: () => void };

// Display <-> API mappings. The UI keeps friendly labels; the API stores canonical tokens (and an
// IANA timezone — the single per-user source of truth shared with the activity report + daily digest).
const CADENCE_TO_API: Record<string, string> = {
  Hourly: 'Hourly',
  'Every 2 hours': 'Every2Hours',
  'Every 4 hours': 'Every4Hours',
  Off: 'Off',
};
const CADENCE_FROM_API: Record<string, string> = {
  Hourly: 'Hourly',
  Every2Hours: 'Every 2 hours',
  Every4Hours: 'Every 4 hours',
  Off: 'Off',
};

const DIGEST_TO_API: Record<'on' | 'activity' | 'off', string> = {
  on: 'On',
  activity: 'OnlyWithActivity',
  off: 'Off',
};
const DIGEST_FROM_API: Record<string, 'on' | 'activity' | 'off'> = {
  On: 'on',
  OnlyWithActivity: 'activity',
  Off: 'off',
};

const TIME_OPTIONS = ['6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '12:00 PM', '5:00 PM'] as const;
const TIME_TO_API: Record<string, string> = {
  '6:00 AM': '06:00',
  '7:00 AM': '07:00',
  '8:00 AM': '08:00',
  '9:00 AM': '09:00',
  '12:00 PM': '12:00',
  '5:00 PM': '17:00',
};
const TIME_FROM_API: Record<string, string> = Object.fromEntries(
  Object.entries(TIME_TO_API).map(([label, hhmm]) => [hhmm, label])
);

// Timezone options as { friendly label, IANA id, browser-locale matcher }.
const TZ = [
  { label: '(GMT-5:00) Eastern Time — US & Canada', iana: 'America/New_York', match: /new_york|toronto|eastern/i },
  { label: '(GMT-8:00) Pacific Time — US & Canada', iana: 'America/Los_Angeles', match: /los_angeles|vancouver|tijuana|pacific/i },
  { label: '(GMT+5:30) India Standard Time', iana: 'Asia/Kolkata', match: /kolkata|calcutta/i },
  { label: '(GMT+0:00) UTC', iana: 'UTC', match: /^utc$|etc\/utc|^gmt/i },
] as const;
const TZ_OPTIONS = TZ.map((t) => t.label);

/** Best-effort map of the browser's IANA timezone to one of the options (defaults to Eastern). */
function seedTimezone(): string {
  let zone = '';
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    zone = '';
  }
  return (TZ.find((t) => t.match.test(zone)) ?? TZ[0]).label;
}

const labelToIana = (label: string): string => (TZ.find((t) => t.label === label) ?? TZ[0]).iana;
const ianaToLabel = (iana: string): string => TZ.find((t) => t.iana === iana)?.label ?? seedTimezone();

export function NotificationsTab({ onClose }: NotificationsTabProps) {
  // Load prefs FIRST so the controls seed straight from them. On reopen/tab-switch SWR returns the
  // cached row synchronously, so the very first render already shows the saved values — no flash of
  // defaults before a post-render hydrate.
  const { data: prefs, mutate: mutatePrefs } = useSWR<any>(endpoints.account.notificationPreferences, fetcher);

  const [cadence, setCadence] = useState(() =>
    prefs ? CADENCE_FROM_API[prefs.ActivityCadence] ?? 'Every 2 hours' : 'Every 2 hours'
  );
  const [digest, setDigest] = useState<'on' | 'activity' | 'off'>(() =>
    prefs ? DIGEST_FROM_API[prefs.DigestMode] ?? 'activity' : 'activity'
  );
  const [time, setTime] = useState(() =>
    prefs?.DigestDeliveryTime ? TIME_FROM_API[prefs.DigestDeliveryTime] ?? '8:00 AM' : '8:00 AM'
  );
  const [tz, setTz] = useState(() => (prefs?.Timezone ? ianaToLabel(prefs.Timezone) : seedTimezone()));
  const [mentions, setMentions] = useState(() => (prefs ? Boolean(prefs.Mentions) : true));
  const [saving, setSaving] = useState(false);

  // If prefs weren't cached at mount (first-ever open), hydrate once the fetch resolves. When they
  // were cached, the initial state already reflects them, so this is a no-op.
  const [hydrated, setHydrated] = useState(() => Boolean(prefs));
  useEffect(() => {
    if (!prefs || hydrated) return;
    setCadence(CADENCE_FROM_API[prefs.ActivityCadence] ?? 'Every 2 hours');
    setDigest(DIGEST_FROM_API[prefs.DigestMode] ?? 'activity');
    if (prefs.DigestDeliveryTime) setTime(TIME_FROM_API[prefs.DigestDeliveryTime] ?? '8:00 AM');
    if (prefs.Timezone) setTz(ianaToLabel(prefs.Timezone));
    setMentions(Boolean(prefs.Mentions));
    setHydrated(true);
  }, [prefs, hydrated]);

  // Following a job IS the subscription, so with zero follows none of these emails can ever arrive.
  // Say so plainly (AC: zero-followed-jobs state messaged, not silently empty).
  const { data: followedJobs = [] } = useSWR<any[]>(endpoints.job_followers.mine, fetcher);
  const noFollows = followedJobs.length === 0;

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const updated = await updateCall(
        endpoints.account.notificationPreferences,
        {
          ActivityCadence: CADENCE_TO_API[cadence] ?? 'Every2Hours',
          DigestMode: DIGEST_TO_API[digest],
          // No delivery time for a digest that never sends.
          DigestDeliveryTime: digest === 'off' ? null : TIME_TO_API[time] ?? null,
          Timezone: labelToIana(tz),
          Mentions: mentions,
        },
        {},
        'PUT'
      );
      // Update the SWR cache with the saved row so reopening the tab shows the new values (not the
      // stale first-load cache). The PUT returns the persisted preferences.
      await mutatePrefs(updated, { revalidate: false });
      toast.success('Saved', { description: 'Notification preferences updated.' });
      onClose();
    } catch {
      toast.error('Failed', { description: 'Could not save your preferences. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Fixed height so switching Profile ↔ Notifications never resizes the dialog (no jump). */}
      <DialogContent sx={{ height: 'min(70vh, 620px)', overflowY: 'auto' }}>
        <Stack spacing={2.5} sx={{ my: 1 }}>
          {/* Scoping banner — following a job is the subscription; stated once, up top. Turns into a
              warning when the user follows nothing, so an empty inbox never looks broken. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              bgcolor: noFollows ? 'warning.lighter' : 'primary.lighter',
              border: '1px solid',
              borderColor: noFollows ? 'warning.light' : 'primary.light',
              borderRadius: 1.5,
              p: '12px 16px',
            }}
          >
            <Iconify
              icon={noFollows ? 'solar:bell-off-linear' : 'solar:info-circle-linear'}
              width={18}
              sx={{ color: noFollows ? 'warning.main' : 'primary.main', flexShrink: 0 }}
            />
            <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: noFollows ? 'warning.darker' : 'primary.dark' }}>
              {noFollows
                ? "You're not following any jobs, so none of these emails will reach you. Follow a job to start receiving updates."
                : "You'll only receive updates for jobs you follow."}
            </Typography>
          </Box>

          {/* Activity report */}
          <Panel>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2.5, flexWrap: 'wrap' }}>
              <Box sx={{ maxWidth: 380 }}>
                <Typography sx={{ fontSize: 16.5, fontWeight: 700, color: 'text.primary' }}>Activity report</Typography>
                <Typography sx={{ fontSize: 13.5, color: 'text.disabled', mt: 0.5, lineHeight: 1.5 }}>
                  A single email covering new applicants, completed interviews, and flagged candidates across your followed
                  jobs.
                </Typography>
              </Box>
              <Segmented value={cadence} onChange={setCadence} options={['Hourly', 'Every 2 hours', 'Every 4 hours', 'Off']} />
            </Box>
            {cadence !== 'Off' && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  mt: 2,
                  bgcolor: 'background.neutral',
                  borderRadius: 1.5,
                  p: '11px 15px',
                  fontSize: 13,
                  color: 'text.secondary',
                  lineHeight: 1.5,
                }}
              >
                <Iconify icon="solar:clock-circle-outline" width={16} sx={{ flexShrink: 0, mt: '2px', color: 'text.secondary' }} />
                <span>
                  Sent only when there&apos;s activity, between <b>7:00 AM and 9:00 PM</b> in your timezone. Overnight
                  activity is included in your first morning report — no emails at night.
                </span>
              </Box>
            )}
          </Panel>

          {/* Daily digest */}
          <Panel>
            <Typography sx={{ fontSize: 16.5, fontWeight: 700, color: 'text.primary', mb: 0.25 }}>Daily digest</Typography>
            <Typography sx={{ fontSize: 13.5, color: 'text.disabled', mb: 2, lineHeight: 1.5 }}>
              The day in review across your followed jobs, including expired interview invites and items needing cleanup.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: digest === 'off' ? 0 : 2.5 }}>
              <DigestOption title="On" sub="Sent every day, even when nothing happened." selected={digest === 'on'} onClick={() => setDigest('on')} />
              <DigestOption title="Only with activity" sub="Sent only on days something happened." selected={digest === 'activity'} onClick={() => setDigest('activity')} />
              <DigestOption title="Off" sub="Never send the daily digest." selected={digest === 'off'} onClick={() => setDigest('off')} />
            </Box>
            {digest !== 'off' && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  select
                  size="small"
                  label="Delivery time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  {TIME_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Timezone"
                  value={tz}
                  onChange={(e) => setTz(e.target.value)}
                  sx={{ flex: '1 1 260px' }}
                >
                  {TZ_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
          </Panel>

          {/* Mentions */}
          <Panel>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2.5 }}>
              <Box>
                <Typography sx={{ fontSize: 16.5, fontWeight: 700, color: 'text.primary' }}>Mentions</Typography>
                <Typography sx={{ fontSize: 13.5, color: 'text.disabled', mt: 0.5, lineHeight: 1.5 }}>
                  Instant email when someone @mentions you in a comment.
                </Typography>
              </Box>
              <Switch checked={mentions} onChange={(e) => setMentions(e.target.checked)} />
            </Box>
          </Panel>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSave}
          disabled={saving}
          sx={{ borderRadius: '14px', px: 3.5, fontWeight: 700 }}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </>
  );
}

/* --------------------------------------------------------------- primitives */

function Panel({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: '20px 22px' }}>
      {children}
    </Box>
  );
}

function Segmented({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <Box sx={{ display: 'inline-flex', bgcolor: 'action.hover', borderRadius: '999px', p: '4px', flexWrap: 'wrap', flexShrink: 0 }}>
      {options.map((o) => {
        const on = value === o;
        return (
          <Box
            key={o}
            component="button"
            type="button"
            onClick={() => onChange(o)}
            sx={{
              border: 'none',
              cursor: 'pointer',
              px: '18px',
              py: '8px',
              borderRadius: '999px',
              fontSize: 13.5,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              transition: 'all .15s ease',
              bgcolor: on ? 'primary.main' : 'transparent',
              color: on ? 'primary.contrastText' : 'text.secondary',
            }}
          >
            {o}
          </Box>
        );
      })}
    </Box>
  );
}

function DigestOption({
  title,
  sub,
  selected,
  onClick,
}: {
  title: string;
  sub: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        minWidth: 150,
        cursor: 'pointer',
        borderRadius: 2,
        p: '13px 15px',
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'primary.lighter' : 'background.paper',
        transition: 'all .15s ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box
          sx={{
            width: 17,
            height: 17,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid',
            borderColor: selected ? 'primary.main' : 'text.disabled',
          }}
        >
          {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />}
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: selected ? 'primary.main' : 'text.primary' }}>
          {title}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: 'text.disabled', lineHeight: 1.4, pl: '25px' }}>{sub}</Typography>
    </Box>
  );
}
